"use client";

import { useCallback, useLayoutEffect, type RefObject } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";
import { useCcScrollEffect } from "@/lib/use-cc-scroll-effect";

/** Nuxt ScrollSmoother is disabled at or below 768px (`pages/index.vue`, `[...slug].vue`). */
const SCROLL_SPEED_PARALLAX_MAX_WIDTH = 768;

/**
 * Vertical offset approximating GSAP ScrollSmoother `data-speed` (effects: true).
 * speed 1 = no parallax; values above 1 move faster than scroll (floating card).
 */
export function getCcScrollSpeedParallaxOffset(
  triggerRect: DOMRect,
  speed: number,
  viewportHeight: number,
  intensity = 0.55,
): number {
  const centerDelta =
    triggerRect.top + triggerRect.height * 0.5 - viewportHeight * 0.5;
  return centerDelta * (1 - speed) * intensity;
}

type UseCcScrollSpeedParallaxOptions = {
  speed: number;
  /** Element whose rect anchors the effect (e.g. showcase stage). */
  triggerRef: RefObject<HTMLElement | null>;
  intensity?: number;
};

/**
 * Applies translate3d parallax to `targetRef` while scrolling (desktop only).
 */
export function useCcScrollSpeedParallax(
  targetRef: RefObject<HTMLElement | null>,
  { speed, triggerRef, intensity = 0.55 }: UseCcScrollSpeedParallaxOptions,
) {
  const prefersReducedMotion = useCcPrefersReducedMotion();

  const clearTransform = useCallback(() => {
    const target = targetRef.current;
    if (!target) return;
    target.style.transform = "";
  }, [targetRef]);

  const update = useCallback(() => {
    const target = targetRef.current;
    const trigger = triggerRef.current;
    if (!target || !trigger || prefersReducedMotion) {
      clearTransform();
      return;
    }

    if (window.innerWidth <= SCROLL_SPEED_PARALLAX_MAX_WIDTH) {
      clearTransform();
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const vh = window.innerHeight;
    if (rect.bottom < 0 || rect.top > vh) {
      return;
    }

    const offset = getCcScrollSpeedParallaxOffset(rect, speed, vh, intensity);
    target.style.transform = `translate3d(0, ${offset}px, 0)`;
  }, [clearTransform, intensity, prefersReducedMotion, speed, targetRef, triggerRef]);

  useCcScrollEffect(update);

  useLayoutEffect(() => {
    update();
    return () => clearTransform();
  }, [clearTransform, update]);
}
